import {
  FieldArray,
  FieldArrayRenderProps,
  Form,
  useFormikContext,
} from 'formik';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { Modal } from '@@/modals';
import { Alert } from '@@/Alert';

import { NotificationChannelConfiguration } from './NotificationChannelConfiguration';
import { AlertSettingsFormValues, NotificationChannel } from './types';

interface Props {
  isInternalAlertManager: boolean;
  onCancel: () => void;
}

export function InnerForm({ isInternalAlertManager, onCancel }: Props) {
  const [lastAddedChannelId, setLastAddedChannelId] = useState<string | null>(
    null
  );
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isSubmitting,
    dirty,
    submitForm,
  } = useFormikContext<AlertSettingsFormValues>();

  return (
    <>
      <Modal.Header title="Edit Alert Settings" />

      <Modal.Body>
        {isInternalAlertManager && (
          <Alert color="info" className="mb-4">
            This is the internal alert manager. The name cannot be modified.
          </Alert>
        )}

        <Form>
          <div className="form-group">
            <FormControl
              label="Name"
              inputId="name-input"
              errors={errors.name && touched.name ? errors.name : undefined}
              required
              tooltip={
                isInternalAlertManager
                  ? 'Internal alert manager name cannot be changed'
                  : undefined
              }
            >
              <Input
                id="name-input"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter alert manager name"
                data-cy="alert-settings-name-input"
                readOnly={isInternalAlertManager}
                disabled={isInternalAlertManager}
              />
            </FormControl>
          </div>

          <div className="form-group">
            <FormControl label="Enabled" inputId="enabled-input">
              <Switch
                id="enabled-input"
                name="enabled"
                checked={values.enabled}
                onChange={(checked) => setFieldValue('enabled', checked)}
                data-cy="alert-settings-enabled-switch"
              />
            </FormControl>
          </div>

          {/* Notification Channels Management */}
          <FieldArray name="notificationChannels">
            {(arrayHelpers) => (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium">
                    Notification Channels ({values.notificationChannels.length})
                  </label>
                  <Button
                    type="button"
                    onClick={() => handleAddChannel(arrayHelpers)}
                    icon={Plus}
                    color="primary"
                    size="small"
                    data-cy="add-notification-channel"
                  >
                    Add Channel
                  </Button>
                </div>
                <p className="text-gray-500 mt-1 mb-4 text-xs">
                  More notification channels will be added over time.
                </p>

                {values.notificationChannels.length === 0 ? (
                  <div className="border-gray-200 th-dark:border-gray-700 flex flex-col items-center justify-center rounded-xl border-2 border-dashed bg-[var(--bg-widget-color)] px-8 py-12">
                    <h3 className="mb-2 text-lg font-medium">
                      No notification channels configured
                    </h3>
                    <p className="text-gray-500 th-dark:text-gray-400 mb-4 max-w-xs text-center">
                      Add notification channels to receive alerts when issues
                      are detected in your environment.
                    </p>
                    <Button
                      type="button"
                      onClick={() => handleAddChannel(arrayHelpers)}
                      icon={Plus}
                      color="primary"
                      size="small"
                      data-cy="add-first-notification-channel"
                    >
                      Add Your First Channel
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {values.notificationChannels.map((channel, index) => (
                      <NotificationChannelConfiguration
                        key={channel.id}
                        index={index}
                        defaultExpanded={channel.id === lastAddedChannelId}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </FieldArray>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          color="default"
          onClick={onCancel}
          data-cy="alert-settings-cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSubmitting || !dirty}
          data-cy="alert-settings-save-button"
          onClick={submitForm}
        >
          {isSubmitting ? 'Saving...' : 'Save Settings'}
        </Button>
      </Modal.Footer>
    </>
  );

  function handleAddChannel(arrayHelpers: FieldArrayRenderProps) {
    const newChannel: NotificationChannel = {
      id: uuidv4(),
      name: 'New Channel',
      type: 'slack',
      enabled: true,
      config: {},
    };
    arrayHelpers.push(newChannel);
    setLastAddedChannelId(newChannel.id);
  }
}
